
import  { useState, useEffect, useContext } from "react";
import "./Settings.css";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import departmenticon from "../../../../assets/Image/departmenticon.png";
import Loader from "../../../../assets/Image/Loader2.gif";
import LoadingOverlay from "../../../common/LoadingBar";
import { useToastHandler } from "../../../common/ToastHandler";
import { ToastContainer } from "react-toastify";
import useSwal from "../../../common/ReusableComponent/useSwal";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import { CustomButton } from "../../../common/ReusableComponent/CustomButton";
import ChipList from "../../../common/ReusableComponent/ChipList";
import { Trash2 } from "lucide-react";
import { fetchAllPaginatedOptions, getBulkResultMessage } from "./settingsConfigUtils";

function ManageDepartments({ embedded = false }) {
  const [departments, setDepartments] = useState([]);
  const [, setNewDepartment] = useState("");
  const [selectedDepartmentList, setSelectedDepartmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  let { user } = useContext(AuthContext);
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();
  const storeId = user.store;
  const { Post, Delete, refreshCache, GetPaginatedData } = useApi();

  const fetchDepartments = async () => {
  setLoading(true);
  try {
    const records = await fetchAllPaginatedOptions(GetPaginatedData, "vendorDepartmentServiceData");
    const departmentsData = records
      .filter((item) => item.all_department !== null || item.custom_type === "department")
      .map((item) => ({
        id: item.id,
        title: item.all_department_detail?.title || item.custom_title || "",
        departmentId: item.all_department
      }));
    setDepartments(departmentsData);
  } catch (error) {
    console.error("Error fetching departments:", error);
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchDepartments();
  }, []);

  const resetInputFields = () => {
    setNewDepartment("");
  };

  const handleDepartmentSelectMultiple = (dept) => {
    if (!dept) return;
    const exists = selectedDepartmentList.some(
      (d) => d.id === dept.id
    );
    if (!exists) {
      setSelectedDepartmentList((prev) => [...prev, dept]);
    }
  };

  const handleRemoveDepartment = (item) => {
    setSelectedDepartmentList((prev) =>
      prev.filter((dept) => dept.id !== item.id)
    );
  };

  const handleSaveDepartment = async () => {
    if (selectedDepartmentList.length === 0) {
      handleToast({
        type: "WARNING",
        message: "Please select department",
      });
      return;
    }
    try {
      const payload = selectedDepartmentList.map((item) => ({
        store: storeId,
        all_expense_type: null,
        all_department: item.isCustom ? null : item.id,
        custom_title: item.isCustom ? item.title : null,
        custom_type: item.isCustom ? "department" : null,
      }));
      await Post("vendorDepartmentServiceData", payload);
      setSelectedDepartmentList([]);
      fetchDepartments();
      handleToast({
        type: "SUCCESS",
        message: "Departments added successfully!",
      });
    } catch (error) {
      console.error(error);
      const errorData = error.response?.data;
      let errorMessage = "Error adding departments";
      
      if (errorData?.total_skipped > 0) {
        errorMessage = errorData.skipped[0].message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      handleToast({
        type: "ERROR",
        message: errorMessage,
      });
    }
  };

  const handleAddAllDefaults = async () => {
    setLoading(true);
    try {
      const options = await fetchAllPaginatedOptions(GetPaginatedData, "allDepartmentData");
      const existingIds = new Set(departments.map((item) => String(item.departmentId)).filter(Boolean));
      const payload = options
        .filter((item) => !existingIds.has(String(item.id)))
        .map((item) => ({ store: storeId, all_expense_type: null, all_department: item.id }));
      if (!payload.length) {
        handleToast({ type: "INFO", message: "All default departments are already added." });
        return;
      }
      const response = await Post("vendorDepartmentServiceData", payload);
      handleToast({ type: "SUCCESS", message: getBulkResultMessage(response, "default departments") });
      setSelectedDepartmentList([]);
      await fetchDepartments();
    } catch (error) {
      handleToast({ type: "ERROR", message: error.response?.data?.message || "Unable to add all default departments." });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      await Delete("vendorDepartmentServiceData", id);
      setDepartments(departments.filter((dept) => dept.id !== id));
      refreshCache("vendorDepartmentServiceData", "get");
      resetInputFields();
      handleToast({
        type: "SUCCESS",
        message: "Department deleted successfully!",
      });
    } catch (error) {
      console.error(
        "Error deleting department:",
        error.response ? error.response.data : error.message
      );
      handleToast({
        type: "ERROR",
        message: "Error deleting department.",
      });
    }
  };


const filteredDepartments = departments || [];
  return (
    <div>
      <LoadingOverlay loading={loading} loader={Loader} />
      <div className={embedded ? "settings-embedded-tool-panel" : "responsive-box"}>
        <div className="input-container">
          <div className="input-row">
            <div className="form-group product-field">
              <PaginatedSearchSelect
                api="allDepartmentData"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt.title || ""}
                onSelect={handleDepartmentSelectMultiple}
                placeholder="Select Department"
                pageSize={10}
                allowCustom
                customOptionLabel={(value) => `Add custom department "${value}"`}
                filterFn={(opt) =>
                  !selectedDepartmentList.some((item) => String(item.id) === String(opt.id)) &&
                  !departments.some((item) => String(item.departmentId) === String(opt.id))
                }
              />
            </div>
           
            <CustomButton
              text="Add Department"
              icon={departmenticon}
              iconPosition="right"
              onClick={handleSaveDepartment}
            />
            <CustomButton text="Add All Defaults" onClick={handleAddAllDefaults} />
          </div>

          <ChipList
            items={selectedDepartmentList}
            onRemove={handleRemoveDepartment}
            displayField="title"
            keyField="id"
          />
        </div>
      </div>

      <div className={embedded ? "settings-embedded-tool-panel settings-embedded-table-panel" : "responsive-box"}>
        <div className="settings-chip-board">
          {filteredDepartments.length === 0 ? (
            <div className="settings-empty-state settings-table-empty">
              <strong>No departments added yet</strong>
              <span>Select departments above to organize vendor and purchase records.</span>
            </div>
          ) : (
            filteredDepartments.map((dept) => (
              <div className="settings-config-chip" key={dept.id}>
                <span>{dept.title || "N/A"}</span>
                <button
                  type="button"
                  className="settings-chip-delete"
                  aria-label={`Delete ${dept.title || "department"}`}
                  onClick={() => handleDeleteDepartment(dept.id)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      {!embedded && <ToastContainer />}
    </div>
  );
}

export default ManageDepartments;
